import { DropdownSelect } from "@factorearth/component-library";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import { FormTemplate, Project, UserForResolver } from "db/definitions";
import React, { useMemo } from "react";
import { sortByAlphaNumericalArray } from "utils/sort";
import filterTemplatesByGroupAndProject from "../../utils/filterTemplatesByGroupAndProject";

interface Props {
	color: Colors;
	formTemplates: FormTemplate[];
	project: Project | null;
	submitCreate: (formTemplate: FormTemplate) => Promise<void>;
	templateGroupFilter: "Show All Templates" | string;
	filter: "group" | "project" | null;
	user: UserForResolver;
}

interface CreateNewInstanceOption extends FormTemplate {
	label: string;
	value: string;
}

function CreateNewInstance(props: Props) {
	const { color, formTemplates, submitCreate, templateGroupFilter, project, user, filter } = props;

	/**
	 * Select the template and submit
	 * @param e event object
	 */
	function handleTemplate(template: unknown) {
		const newTemplate = template as CreateNewInstanceOption;
		let id = newTemplate.id;
		const foundTemplate = formTemplates.find((obj) => obj.id === id);
		if (foundTemplate) submitCreate(foundTemplate);
	}

	/**
	 * Setup the template options for the select
	 * @returns html
	 */
	const options = useMemo(() => {
		/**
		 * sort method callback for FormTemplates
		 * @param a first one
		 * @param b second one
		 * @returns sort data.
		 */
		function sortTemplates(a: FormTemplate, b: FormTemplate) {
			if (a.template > b.template) {
				return -1;
			}
			if (a.template < b.template) {
				return 1;
			}
			return 0;
		}

		let formTemplatesSorted = formTemplates.filter((ft) => ft.publish).sort(sortTemplates);

		if (filter && project) {
			// Keep in mind that this could return an empty array if the filters completely filter everything out
			formTemplatesSorted = filterTemplatesByGroupAndProject(formTemplatesSorted, filter === "group", filter === "project", user, project);
		}

		let grouped = formTemplatesSorted.reduce((acc: Record<string, FormTemplate[]>, template) => {
			const accTemplateGroup = acc[template.formTemplateGroup] || [];
			const labelName = template.templateName ? template.templateName : template.template;
			const versionName = template.versionName ? ` - ${template.versionName}` : "";
			const label = `${labelName}${versionName}`;
			return {
				...acc,
				[template.formTemplateGroup]: [{ label: label, value: label, ...template }, ...accTemplateGroup],
			};
		}, {});
		if (templateGroupFilter !== "Show All Templates") {
			const key = templateGroupFilter;
			// If there are no sorted templates because they were all filtered out, grouped will be an empty object, so I have this
			// cast to an empty array in that case
			grouped = {
				[key]: grouped[templateGroupFilter] || [],
			};
		}
		const keys = sortByAlphaNumericalArray({ array: Object.keys(grouped) });
		const final = keys.map((groupName) => ({
			label: groupName,
			options: grouped[groupName],
		}));
		return final;
	}, [formTemplates, templateGroupFilter, filter, project, user]);

	return (
		<DropdownSelect
			name="template"
			id="template"
			data-test="select-template-instance"
			onChange={handleTemplate}
			value={{ label: "Select a Template..", value: "SELECT_A_TEMPLATE" }}
			options={options}
			isSearchable={true}
			menuPlacement="auto"
			minMenuHeight={300}
			classNamePrefix={"react-select"}
			placeholder={"Select a Template..."}
			// @ts-ignore
			colorPalette={color}
		/>
	);
}

export default CreateNewInstance;
